import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import { useLocation, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { companyAPI, auditAPI, aiBuilderAPI } from '../../services/api';
import { useCompany } from '../../contexts/CompanyContext';

function prettify(segment: string) {
  // Replace dashes/underscores with spaces and capitalize
  return segment.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const BreadcrumbsNav: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { companies, selectedCompany } = useCompany();  // Get companies from context to avoid API calls
  const pathnames = useMemo(() => location.pathname.split('/').filter(x => x), [location.pathname]);
  const [companyNames, setCompanyNames] = useState<Record<string, string>>({});
  const [sessionCompanyInfo, setSessionCompanyInfo] = useState<{ companyId: number; companyName: string } | null>(null);
  const [templateNames, setTemplateNames] = useState<Record<string, string>>({});
  const fetchedIdsRef = useRef<Set<string>>(new Set());  // Track which IDs we've already fetched
  const fetchedSessionsRef = useRef<Set<string>>(new Set());  // Track which session IDs we've fetched
  const fetchedTemplatesRef = useRef<Set<string>>(new Set());  // Track which template IDs we've fetched

  // Effect to resolve company names - prefer context, fallback to API
  useEffect(() => {
    const resolveCompanyNames = async () => {
      const newCompanyNames: Record<string, string> = {};
      const companyIdsToFetch: string[] = [];

      // Check path segments for company IDs
      for (let i = 0; i < pathnames.length; i++) {
        const segment = pathnames[i];
        const prevSegment = i > 0 ? pathnames[i - 1] : '';
        
        // Check if this is a company ID (previous segment is "company" and current is a number)
        if (prevSegment === 'company' && /^\d+$/.test(segment)) {
          companyIdsToFetch.push(segment);
        }
      }

      // Check for company parameter in URL
      const companyParam = searchParams.get('company');
      if (companyParam && /^\d+$/.test(companyParam)) {
        companyIdsToFetch.push(companyParam);
      }

      // Resolve company names - try context first, then API
      for (const companyIdStr of companyIdsToFetch) {
        // Skip if we already have this company name
        if (companyNames[companyIdStr]) continue;
        
        const companyId = parseInt(companyIdStr);
        
        // First, try to find in context (no API call needed!)
        const companyFromContext = companies.find(c => c.id === companyId);
        if (companyFromContext) {
          newCompanyNames[companyIdStr] = companyFromContext.name;
          continue;
        }
        
        // Skip if we've already fetched this ID (prevents duplicate calls)
        if (fetchedIdsRef.current.has(companyIdStr)) continue;
        fetchedIdsRef.current.add(companyIdStr);
        
        // Fallback to API call
        try {
          const company = await companyAPI.getCompany(companyId);
          newCompanyNames[companyIdStr] = company.name;
          console.log('✅ Loaded company name for breadcrumb:', company.name);
        } catch (error) {
          console.warn(`Failed to fetch company ${companyId}:`, error);
          newCompanyNames[companyIdStr] = `Company ${companyId}`;
        }
      }

      // Only update state if we found any new company names
      if (Object.keys(newCompanyNames).length > 0) {
        setCompanyNames(prev => ({ ...prev, ...newCompanyNames }));
      }
    };

    resolveCompanyNames();
  }, [location.pathname, pathnames, searchParams, companies, companyNames]);

  // Effect to resolve session company info for audit-analysis and audit-session pages
  useEffect(() => {
    const resolveSessionCompany = async () => {
      // Check if we're on audit-analysis or audit-session page
      const isAuditAnalysis = pathnames[0] === 'audit-analysis' && pathnames[1];
      const isAuditSession = pathnames[0] === 'audit-session' && pathnames[1];
      
      if (!isAuditAnalysis && !isAuditSession) {
        setSessionCompanyInfo(null);
        return;
      }
      
      const sessionId = pathnames[1];
      
      // If we have selectedCompany in context, use that
      if (selectedCompany) {
        setSessionCompanyInfo({
          companyId: selectedCompany.id,
          companyName: selectedCompany.name,
        });
        return;
      }
      
      // Skip if we've already fetched this session
      if (fetchedSessionsRef.current.has(sessionId)) return;
      fetchedSessionsRef.current.add(sessionId);
      
      // Fetch session to get company info
      try {
        const session = await auditAPI.getAuditSession(parseInt(sessionId));
        if (session && session.company) {
          // Try to get company name from context first
          const companyFromContext = companies.find(c => c.id === session.company);
          if (companyFromContext) {
            setSessionCompanyInfo({
              companyId: session.company,
              companyName: companyFromContext.name,
            });
          } else if (session.company_name) {
            setSessionCompanyInfo({
              companyId: session.company,
              companyName: session.company_name,
            });
          } else {
            // Fetch company name
            const company = await companyAPI.getCompany(session.company);
            setSessionCompanyInfo({
              companyId: session.company,
              companyName: company.name,
            });
          }
        }
      } catch (error) {
        console.warn('Failed to fetch session company info:', error);
      }
    };
    
    resolveSessionCompany();
  }, [pathnames, selectedCompany, companies]);

  // Effect to resolve template names for template editor/view pages
  useEffect(() => {
    const resolveTemplateNames = async () => {
      // Check if we're on a template editor/view page
      const isTemplateEditor = pathnames[0] === 'admin' && 
                               pathnames[1] === 'templates' && 
                               (pathnames[2] === 'edit' || pathnames[2] === 'view') && 
                               pathnames[3];
      
      if (!isTemplateEditor) {
        return;
      }
      
      const templateId = pathnames[3];
      
      // Skip if we've already fetched this template
      if (fetchedTemplatesRef.current.has(templateId)) return;
      fetchedTemplatesRef.current.add(templateId);
      
      // Fetch template to get name
      try {
        const template = await aiBuilderAPI.getTemplate(parseInt(templateId));
        if (template && template.name) {
          setTemplateNames(prev => ({
            ...prev,
            [templateId]: template.name,
          }));
        }
      } catch (error) {
        console.warn('Failed to fetch template name:', error);
      }
    };
    
    resolveTemplateNames();
  }, [pathnames]);

  const getBreadcrumbLabel = (segment: string, index: number) => {
    const prevSegment = index > 0 ? pathnames[index - 1] : '';
    
    // If this is a company ID, return the company name
    if (prevSegment === 'company' && /^\d+$/.test(segment)) {
      return companyNames[segment] || `Loading...`;
    }
    
    // Special case: template ID after edit/view
    if ((prevSegment === 'edit' || prevSegment === 'view') && /^\d+$/.test(segment)) {
      return templateNames[segment] || 'Loading...';
    }
    
    // Special case: "companies" should display as "Company" for consistency
    if (segment === 'companies') {
      return 'Company';
    }
    
    // Special case: audit-analysis with session ID
    if (segment === 'audit-analysis') {
      return 'Review Insights';
    }
    
    // Special case: audit-session with session ID
    if (segment === 'audit-session') {
      return 'Review Session';
    }

    // Special case: company audits page (URL segment) should display as "Reviews"
    if (segment === 'audits') {
      return 'Reviews';
    }

    // Special case: audit start flow route segments should display as "Review"
    if (segment === 'audit') {
      return 'Review';
    }

    if (prevSegment === 'audit' && segment === 'start') {
      return 'Start Review';
    }

    // Special case: audit results route label
    if (segment === 'audit-results') {
      return 'Review Results';
    }
    
    // Special case: session ID after audit-analysis or audit-session
    if ((prevSegment === 'audit-analysis' || prevSegment === 'audit-session') && /^\d+$/.test(segment)) {
      return `Session #${segment}`;
    }
    
    // Otherwise, prettify the segment
    return prettify(segment);
  };

  // Special handling for pages with company context
  const companyParam = searchParams.get('company');
  const highlightParam = searchParams.get('highlight');
  const isAuditResultsWithCompany = location.pathname === '/audit-results' && companyParam;
  const isAdminCompaniesWithHighlight = location.pathname === '/admin/companies' && highlightParam;
  
  // Check for company settings page
  const isCompanySettingsPage = pathnames.length === 3 && pathnames[0] === 'company' && pathnames[2] === 'settings';
  
  // Check for audit-analysis or audit-session pages
  const isAuditAnalysisPage = pathnames[0] === 'audit-analysis' && pathnames[1];
  const isAuditSessionPage = pathnames[0] === 'audit-session' && pathnames[1];
  
  // Check for AI Builder page
  const isAIBuilderPage = pathnames[0] === 'ai-builder';
  const isExcelImportPage = pathnames[0] === 'ai-builder' && pathnames[1] === 'excel-import';

  return (
    <Breadcrumbs aria-label="breadcrumb" sx={{ my: 2, ml: 3 }}>
      <Link component={RouterLink} underline="hover" color="inherit" to="/">
        Home
      </Link>
      
      {isAuditAnalysisPage && sessionCompanyInfo ? [
        // Special breadcrumb for audit analysis with company context
        <Link key="company-parent" component={RouterLink} underline="hover" color="inherit" to="/companies">
          Company
        </Link>,
        <Link key="company" component={RouterLink} underline="hover" color="inherit" to={`/company/${sessionCompanyInfo.companyId}`}>
          {sessionCompanyInfo.companyName}
        </Link>,
        <Link key="audits" component={RouterLink} underline="hover" color="inherit" to={`/company/${sessionCompanyInfo.companyId}/audits`}>
          Reviews
        </Link>,
        <Typography key="analysis" color="text.primary">
          Analysis #{pathnames[1]}
        </Typography>
      ] : isAuditSessionPage && sessionCompanyInfo ? [
        // Special breadcrumb for audit session with company context
        <Link key="company-parent" component={RouterLink} underline="hover" color="inherit" to="/companies">
          Company
        </Link>,
        <Link key="company" component={RouterLink} underline="hover" color="inherit" to={`/company/${sessionCompanyInfo.companyId}`}>
          {sessionCompanyInfo.companyName}
        </Link>,
        <Link key="audits" component={RouterLink} underline="hover" color="inherit" to={`/company/${sessionCompanyInfo.companyId}/audits`}>
          Reviews
        </Link>,
        <Typography key="session" color="text.primary">
          Session #{pathnames[1]}
        </Typography>
      ] : isAuditResultsWithCompany ? [
        // Special breadcrumb for audit results with company context
        <Link key="company-parent" component={RouterLink} underline="hover" color="inherit" to="/companies">
          Company
        </Link>,
        <Link key="company" component={RouterLink} underline="hover" color="inherit" to={`/company/${companyParam}`}>
          {companyNames[companyParam] || `Company ${companyParam}`}
        </Link>,
        <Typography key="audit-results" color="text.primary">
          Review Results
        </Typography>
      ] : isAdminCompaniesWithHighlight ? [
        // Special breadcrumb for admin companies with highlight context
        <Link key="company-parent" component={RouterLink} underline="hover" color="inherit" to="/companies">
          Company
        </Link>,
        <Link key="company" component={RouterLink} underline="hover" color="inherit" to={`/company/${highlightParam}`}>
          {companyNames[highlightParam] || `Company ${highlightParam}`}
        </Link>,
        <Typography key="admin-management" color="text.primary">
          Company Management
        </Typography>
      ] : isCompanySettingsPage ? [
        // Special breadcrumb for company settings page
        <Link key="company-parent" component={RouterLink} underline="hover" color="inherit" to="/companies">
          Company
        </Link>,
        <Link key="company" component={RouterLink} underline="hover" color="inherit" to={`/company/${pathnames[1]}`}>
          {companyNames[pathnames[1]] || `Company ${pathnames[1]}`}
        </Link>,
        <Typography key="settings" color="text.primary">
          Settings
        </Typography>
      ] : isExcelImportPage ? [
        // Special breadcrumb for Excel import page to show as Admin section
        <Link key="admin" component={RouterLink} underline="hover" color="inherit" to="/admin/companies">
          Admin
        </Link>,
        <Link key="templates" component={RouterLink} underline="hover" color="inherit" to="/admin/templates">
          Templates
        </Link>,
        <Typography key="excel-import" color="text.primary">
          Import from Excel
        </Typography>
      ] : isAIBuilderPage ? [
        // Special breadcrumb for AI Builder page to show as Admin section
        <Link key="admin" component={RouterLink} underline="hover" color="inherit" to="/admin/companies">
          Admin
        </Link>,
        <Link key="templates" component={RouterLink} underline="hover" color="inherit" to="/admin/templates">
          Templates
        </Link>,
        <Typography key="ai-builder" color="text.primary">
          AI Builder
        </Typography>
      ] : (
        // Regular breadcrumb generation
        pathnames.map((value, index) => {
          const to = '/' + pathnames.slice(0, index + 1).join('/');
          const isLast = index === pathnames.length - 1;
          const label = getBreadcrumbLabel(value, index);
          
          return isLast ? (
            <Typography color="text.primary" key={to}>
              {label}
            </Typography>
          ) : (
            <Link component={RouterLink} underline="hover" color="inherit" to={to} key={to}>
              {label}
            </Link>
          );
        })
      )}
    </Breadcrumbs>
  );
};

export default BreadcrumbsNav; 